import { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Card, Image, Modal } from 'react-bootstrap';
import api from './api/axiosInstance';
import icValidationError from './assets/img/ic-danger.svg';
import icList from './assets/img/ic-list.svg';
import icNews from './assets/img/ic-news.svg';
import icUser from './assets/img/ic-user-circle.svg';
import icStructure from './assets/img/ic-structure.svg';
import icStatus from './assets/img/ic-doc-refresh.svg';
import icSucess from './assets/img/ic-celebration-circle.svg';
import icDownload from './assets/img/ic-file-download.svg';


const TicketAddDetails = ({ formData, handleChange, handleGeneralSubmit, isEditPage, errors}) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // If adding, fields start enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // Store initial values
    const [platformAdmins, setPlatformAdmins] = useState([]);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [ownersError, setOwnersError] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);

    const baseUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '');

    const isImageAttachment = (filename) =>
        /\.(jpg|jpeg|png|webp)$/i.test(filename || '');

    const allAttachments = [
        ...(formData.attachments || []).map((file) => ({
            ...file,
            source: 'Initial request',
        })),
        ...(formData.activity || []).flatMap((entry) =>
            (entry.attachments || []).map((file) => ({
                ...file,
                source: entry?.createdBy
                    ? `${entry.createdBy.firstName || ''} ${entry.createdBy.familyName || ''}`.trim() || 'Reply'
                    : 'Reply',
            }))
        ),
    ];

    const handleDownload = async (url, filename) => {
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(blobUrl);
        } catch {
            window.open(url, '_blank');
        }
    };

    // Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // Store previous values
    };

    const getOwnerLabel = () => {
        if (!formData?.owner) return 'N/A';
        const ownerUser = platformAdmins.find(u => u._id === formData.owner);
        return ownerUser
            ? `${ownerUser.firstName} ${ownerUser.familyName}`
            : 'N/A';
    };

    // Fetch Platform Admins
    useEffect(() => {
        const fetchPlatformAdmins = async () => {
            try {
                const res = await api.get(`/api/users/platform-admins`);
                const admins = Array.isArray(res.data) ? res.data : [];
                setPlatformAdmins(admins);
                setOwnersError('');
            } catch (error) {
                setOwnersError('Unable to load ticket owners.');
            }
        };

        fetchPlatformAdmins();
    }, []);


    // Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        if (typeof handleGeneralSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleGeneralSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
            setShowSuccessModal(true);
        } else {
            console.error('Error: handleGeneralSubmit is not a function.');
        }
    };

    // Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } }); // Restore previous values
    };
    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">{isEditPage ? 2 : 1}. </span>Ticket details</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <Row>
                            {/* Form Fields */}
                            <Col md={12}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="priority">
                                            <Form.Label>Priority</Form.Label>
                                            <Form.Select
                                                    name="priority"
                                                    value={formData.priority || ''}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    isInvalid={!!errors.priority}
                                                >
                                                    <option value="">Please select</option>
                                                    <option value="Low">Low</option>
                                                    <option value="Medium">Medium</option>
                                                    <option value="High">High</option>
                                                    <option value="Critical">Critical</option>
                                            </Form.Select>
                                            {/* ✅ Custom error icon + tooltip */}
                                            {(!!errors.priority) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.priority}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.priority}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="ticketClass">
                                            <Form.Label>Class</Form.Label>
                                            <Form.Select
                                                    name="ticketClass"
                                                    value={formData.ticketClass || ''}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    isInvalid={!!errors.ticketClass}
                                                >
                                                    <option value="">Please select</option>
                                                    <option value="Incident">Incident</option>
                                                    <option value="Support req.">Support req.</option>
                                                    <option value="Advisory req.">Advisory req.</option>
                                                    <option value="Lifecycle task">Lifecycle task</option>
                                                    <option value="Special task">Special task</option>
                                                    <option value="Improvement ideas">Improvement ideas</option>
                                                    <option value="Development tasks">Development tasks</option>
                                            </Form.Select>
                                            {/* ✅ Custom error icon + tooltip */}
                                            {(!!errors.ticketClass) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.ticketClass}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.ticketClass}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="status">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                    name="status"
                                                    value={formData.status || ''}
                                                    onChange={handleChange}
                                                    disabled={!isEditing}
                                                    isInvalid={!!errors.status}
                                                >
                                                    <option value="">Please select</option>
                                                    <option value="Open">Open</option>
                                                    <option value="Resolved">Resolved</option>
                                                    <option value="Archived">Archived</option>
                                            </Form.Select>
                                            {/* ✅ Custom error icon + tooltip */}
                                            {(!!errors.status) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.status}
                                                        className="custom-error-icon"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.status}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="validated" controlId="owner">
                                            <Form.Label>Ticket owner</Form.Label>
                                            <Form.Select
                                                name="owner"
                                                value={formData.owner || ''}
                                                onChange={handleChange}
                                                disabled={!isEditing || !!ownersError}
                                                isInvalid={!!errors.owner}
                                            >
                                                <option value="">Please select</option>
                                                {platformAdmins.map((admin) => (
                                                    <option key={admin._id} value={admin._id}>
                                                        {admin.firstName} {admin.familyName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                            {(errors.owner || ownersError) && (
                                                <div className="custom-error-icon-wrapper">
                                                    <img
                                                        src={icValidationError}
                                                        alt={errors.owner || ownersError}
                                                        className="custom-error-icon me-2"
                                                    />
                                                    <div className="custom-tooltip">
                                                        {errors.owner || ownersError}
                                                    </div>
                                                </div>
                                            )}
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <Form.Group controlId="businessPurpose">
                                            <Form.Label>Business purpose</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                name="businessPurpose"
                                                value={formData.businessPurpose || ''}
                                                onChange={handleChange}
                                                disabled={!isEditing}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icList} alt="Priority" />
                                        </span>
                                        <span className="text">
                                            Priority
                                            <br />
                                            <strong>
                                                {formData.priority || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icStructure} alt="Class" />
                                        </span>
                                        <span className="text">
                                            Class
                                            <br />
                                            <strong>
                                                {formData.ticketClass || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icStatus} alt="Status" />
                                        </span>
                                        <span className="text">
                                            Status
                                            <br />
                                            <strong>
                                                {formData.status || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icUser} alt="Ticket owner" />
                                        </span>
                                        <span className="text">
                                            Ticket owner
                                            <br />
                                            <strong>
                                                {getOwnerLabel()}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image src={icNews} alt="Short description" />
                                        </span>
                                        <span className="text">
                                            Short description
                                            <br />
                                            <strong>
                                                {formData.businessPurpose || "N/A"}
                                            </strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            {allAttachments.length > 0 && (() => {
                                const images = allAttachments.filter(a => isImageAttachment(a.filename || a.url));
                                const files  = allAttachments.filter(a => !isImageAttachment(a.filename || a.url));
                                return (
                                    <>
                                        {images.length > 0 && (
                                            <Row className="mt-3">
                                                <Col md={12}>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {images.map((a, idx) => {
                                                            const url = `${baseUrl}${a.url}`;
                                                            const filename = a.originalName || a.filename || 'Download';
                                                            return (
                                                                <img
                                                                    key={idx}
                                                                    src={url}
                                                                    alt={filename}
                                                                    onClick={() => setPreviewUrl(url)}
                                                                    style={{
                                                                        width: 64,
                                                                        height: 64,
                                                                        objectFit: 'cover',
                                                                        borderRadius: 6,
                                                                        border: '1px solid #c0d9ee',
                                                                        cursor: 'pointer',
                                                                        display: 'block',
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                        {files.length > 0 && (
                                            <Row className="mt-2">
                                                <Col md={12}>
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {files.map((a, idx) => {
                                                            const url = `${baseUrl}${a.url}`;
                                                            const filename = a.originalName || a.filename || 'Download';
                                                            return (
                                                                <button
                                                                    key={idx}
                                                                    type="button"
                                                                    onClick={() => handleDownload(url, filename)}
                                                                    className="d-inline-flex align-items-center gap-1 btn btn-link p-0"
                                                                >
                                                                    <Image src={icDownload} alt="" />
                                                                    <span style={{ fontSize: '1.2rem' }}>{filename}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </Col>
                                            </Row>
                                        )}
                                    </>
                                );
                            })()}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Success Modal */}
            <Modal show={showSuccessModal} onHide={() => setShowSuccessModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Image
                        src={icSucess}
                        alt=""
                        className="d-block m-auto mb-4"
                    />
                    <h2 className="text-center mb-3">Ticket details Updated</h2>
                    <p className="text-center">Ticket details have been uploaded successfully</p>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-center">
                    <Button variant="primary" onClick={() => setShowSuccessModal(false)}>
                        Go back to the ticket
                    </Button>
                </Modal.Footer>
            </Modal>
            
            <Modal show={!!previewUrl} onHide={() => setPreviewUrl(null)} centered size="lg">
                <Modal.Header closeButton />
                <Modal.Body className="text-center p-2">
                    <img
                        src={previewUrl}
                        alt="Preview"
                        style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 6 }}
                    />
                </Modal.Body>
                <Modal.Footer className="justify-content-center">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleDownload(previewUrl, previewUrl?.split('/').pop())}
                    >
                        Download
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default TicketAddDetails;
